import {Inject, Injectable} from '@angular/core';
import {Observable, ReplaySubject} from 'rxjs';
import {Uuid} from '../interface/response/uuid';
import {first, take} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {CountedAndPaginated} from '../interface/response/counted-and-paginated';
import {Template} from '../interface/template';
import {FilterService} from './filter.service';
import {DOCUMENT} from '@angular/common';
import {TemplatingConfig} from '../interface/templating-config';

@Injectable({
  providedIn: 'root'
})
export class TemplateService {
  private readonly templateListSubject: ReplaySubject<CountedAndPaginated<Template>>
    = new ReplaySubject<CountedAndPaginated<Template>>(1);

  constructor(
    private httpClient: HttpClient,
    private filterService: FilterService,
    @Inject(DOCUMENT) private document: Document,
  ) { }

  sendForm(formData: object, templateId?: string): Observable<Uuid> {
    if (templateId) {
      return this.httpClient.put(`/rest/crm/template/${templateId}`, formData)
        .pipe(
          take(1)
        ) as Observable<Uuid>;
    } else {
      return this.httpClient.post('/rest/crm/template', formData)
        .pipe(
          take(1)
        ) as Observable<Uuid>;
    }
  }

  public loadList(page: number, pageSize: number, filters: object): void {
    this.httpClient.get(`/rest/crm/template?page=${page + 1}&pageSize=${pageSize}&` + this.filterService.parseFilters(filters))
      .pipe(
        first()
      )
      .subscribe((result: CountedAndPaginated<Template>) => {
        this.templateListSubject.next(result);
      });
  }

  public getTemplateListSubject(): Observable<CountedAndPaginated<Template>> {
    return this.templateListSubject;
  }

  public downloadFile(templateId: string): void {
    this.document.location.href = `/rest/crm/template/${templateId}/downloadFile`;
  }

  public deleteFile(uuid: any): Observable<any> {
    return this.httpClient.delete(`/rest/crm/template/${uuid}`)
      .pipe(first()) as Observable<any>;
  }

  public getTemplatingConfig(): Observable<TemplatingConfig> {
    return this.httpClient.get(`/rest/crm/templatingConfig`)
      .pipe(first()) as Observable<TemplatingConfig>;
  }

  public generate(param: {
    contract?: string;
    registration?: string;
    deregistrationPack?: string;
    ofwca?: string;
    type: number;
    onlyNew?: boolean;
    onlyAuthorizations?: boolean;
    onlyPowersOfAttorney?: boolean;
    partner?: string;
    insuranceCompanyIds?: number[];
    deregistrationPackIds?: string[];
    personalDataChange?: string;
    documentsView?: string;
  }): Observable<any> {
    return this.httpClient
        .post(`/rest/crm/template/method/generate`, param)
        .pipe(first());
  }

  public generateAuthorization(param: { registration: string; type: number }): Observable<any> {
    return this.httpClient
      .get(
        `/rest/crm/registration/${param.registration}/generateAuthorization?type=${param.type}`,
        {responseType: 'blob', observe: 'response'}
      )
      .pipe(first()) as Observable<any>;
  }

  public get(templateId: string): Observable<Template> {
    return this.httpClient.get(`/rest/crm/template/${templateId}`)
      .pipe(first()) as Observable<Template>;
  }

  downloadReport(filters: any): void {
    this.document.location.href = `/rest/crm/template/method/report?` + this.filterService.parseFilters(filters);
  }
}
